import React, { ReactNode } from 'react'

import { StepIcon, StepIconProps, SxProps } from '@mui/material'

import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Collapse from 'components_new/atoms/Collapse'
import Icon, { IconProps } from 'components_new/atoms/Icon'
import Step from 'components_new/atoms/Stepper/Step'
import StepLabel from 'components_new/atoms/Stepper/StepLabel'
import Stepper from 'components_new/atoms/Stepper'
import Text from 'components_new/atoms/Text'

type TWizardStep = {
  action?: {
    endIcon?: IconProps
    // return true to advance or false to prevent advancement:
    onClick?: () => boolean
    title: string
  }
  content: ReactNode
  completedIcon?: Pick<IconProps, 'name'>
  completedTitle?: string
  description?: string
  focused?: boolean
  title: string
}

interface StepperWizardProps {
  active: number
  completed?: ReactNode
  setActive: React.Dispatch<React.SetStateAction<number>>
  steps: TWizardStep[]
  sx?: SxProps
}

const StepperWizard = (props: StepperWizardProps) => {
  const { active, completed, setActive, steps, sx } = props

  const { t } = useTranslation()

  const handleSetActive = (currentStep: number) => {
    const action = steps[currentStep].action

    if (action?.onClick) {
      const result = action.onClick()

      if (result === false) return
    }

    const next = active + 1

    if (next < steps.length + 1) {
      setActive(active + 1)
    }
  }

  const isCompleted = active === steps.length

  // success
  return (
    <Box sx={sx}>
      <Stepper activeStep={active} sx={isCompleted ? undefined : { mb: 3 }}>
        {steps.map((step, i) => {
          return (
            <Step key={i}>
              <StepLabel
                StepIconComponent={CustomStepIcon}
                StepIconProps={{ completedIcon: step.completedIcon }}
              >
                {i < active && // "completed"
                step.completedTitle
                  ? step.completedTitle
                  : step.title}
              </StepLabel>
            </Step>
          )
        })}
      </Stepper>

      {completed ? (
        <Collapse in={isCompleted} unmountOnExit={true}>
          <StepContainer>{completed}</StepContainer>
        </Collapse>
      ) : null}

      {!isCompleted
        ? steps.map((step, i) => {
          return (
            <Collapse key={i} in={i === active} unmountOnExit={true}>
              <StepContainer focused={step.focused}>
                {step.description ? (
                  <Text
                    color="text.secondary"
                    sx={{ maxWidth: 'content', mb: 2 }}
                    variant="body1"
                  >
                    {step.description}
                  </Text>
                ) : null}

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    mb: 2
                  }}
                >
                  {step.content}
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end'
                  }}
                >
                  <Button
                    endIcon={
                      step.action?.endIcon ? (
                        <Icon {...step.action.endIcon} />
                      ) : i === steps.length - 1 ? undefined : (
                        <Icon name="ArrowForward" />
                      )
                    }
                    onClick={() => handleSetActive(i)}
                    variant="contained"
                  >
                    {step.action?.title ?? t('global', 'next')}
                  </Button>
                </Box>
              </StepContainer>
            </Collapse>
          )
        })
        : null}
    </Box>
  )
}

export default StepperWizard

type CustomStepIconProps = StepIconProps & {
  completedIcon?: Pick<IconProps, 'name'>
}

const CustomStepIcon = (props: CustomStepIconProps) => {
  const { completed, completedIcon, icon, ...rest } = props

  if (completed)
    return (
      <Box
        sx={{
          alignItems: 'center',
          backgroundColor: 'success.main',
          borderRadius: '50%',
          color: 'success.contrastText',
          display: 'flex',
          fontSize: '1.6rem',
          height: 24,
          justifyContent: 'center',
          width: 24
        }}
      >
        <Icon
          color="inherit"
          name={completedIcon?.name ?? 'Check'}
          sx={{ display: 'block', fontSize: '0.6em' }}
        />
      </Box>
    )

  return <StepIcon completed={completed} icon={icon} {...rest} />
}

interface StepContainerProps {
  children: ReactNode
  focused?: TWizardStep['focused']
}

const StepContainer = (props: StepContainerProps) => {
  const { children, focused } = props

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
        ...(focused ? { borderColor: 'primary.main' } : null)
      }}
    >
      {children}
    </Box>
  )
}
